import {
  $,
  appState,
} from './app-state';
import {
  clearFinishedFileTransfers,
  toggleFileTransfers,
  closeFileTransfers,
  setFileDropActive,
  sendWebFiles,
  retryFileTransfer,
  requestFileTransferSource,
  takeFileTransferSourceSelection,
  updateFileTransferButton,
  renderFileTransfers,
  restorePersistedFileTasks,
} from './file-transfer';

// 独立拆分文件传输的 DOM 事件绑定，避免 file-transfer.ts 超过单文件行数上限。
function bindFileTransfer(): void {
  const button = $('tb-file') as HTMLButtonElement;
  const input = $('tb-file-input') as HTMLInputElement;
  const stage = $('stage');
  const transferList = $('file-transfer-list');
  const transferToggle = $('file-transfers-toggle') as HTMLButtonElement;
  const transferClose = $('file-transfers-close') as HTMLButtonElement;
  $('file-transfers-clear').addEventListener('click', clearFinishedFileTransfers);
  transferToggle.addEventListener('click', toggleFileTransfers);
  transferClose.addEventListener('click', () => closeFileTransfers());
  transferList.addEventListener('click', (event) => {
    const target = event.target as HTMLElement | null;
    const retryButton = target?.closest<HTMLButtonElement>('[data-file-retry]');
    const retryId = retryButton?.dataset.fileRetry;
    if (retryButton && retryId) {
      event.preventDefault();
      if (retryButton.textContent === '选择文件恢复') {
        requestFileTransferSource(retryId);
        input.click();
      } else {
        void retryFileTransfer(retryId);
      }
      return;
    }
    const cancelButton = target?.closest<HTMLButtonElement>('[data-file-cancel]');
    const id = cancelButton?.dataset.fileCancel;
    if (!cancelButton || !id) return;
    event.preventDefault();
    appState.controller?.cancelFileTransfer(id);
  });
  window.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !appState.fileTransferPanelOpen) return;
    event.preventDefault();
    closeFileTransfers();
  });

  button.addEventListener('click', () => {
    if (appState.sessionMode === 'view') return;
    if (!appState.fileTransferBusy) input.click();
  });
  input.addEventListener('change', () => {
    const files = Array.from(input.files || []);
    input.value = '';
    const pendingId = takeFileTransferSourceSelection();
    if (pendingId) {
      if (files[0]) void retryFileTransfer(pendingId, files[0]);
      return;
    }
    void sendWebFiles(files);
  });

  const hasFiles = (dataTransfer: DataTransfer | null): boolean =>
    Boolean(dataTransfer && Array.from(dataTransfer.types).includes('Files'));
  const clearDropState = (): void => {
    appState.fileDragDepth = 0;
    setFileDropActive(false);
  };

  stage.addEventListener('dragenter', (event) => {
    const dragEvent = event as DragEvent;
    if (appState.sessionMode === 'view') return;
    if (!hasFiles(dragEvent.dataTransfer)) return;
    dragEvent.preventDefault();
    dragEvent.stopPropagation();
    appState.fileDragDepth += 1;
    setFileDropActive(true);
  });
  stage.addEventListener('dragover', (event) => {
    const dragEvent = event as DragEvent;
    if (appState.sessionMode === 'view') return;
    if (!hasFiles(dragEvent.dataTransfer)) return;
    dragEvent.preventDefault();
    dragEvent.stopPropagation();
    if (dragEvent.dataTransfer) dragEvent.dataTransfer.dropEffect = 'copy';
    setFileDropActive(true);
  });
  stage.addEventListener('dragleave', (event) => {
    const dragEvent = event as DragEvent;
    if (appState.sessionMode === 'view') return;
    if (!hasFiles(dragEvent.dataTransfer)) return;
    dragEvent.preventDefault();
    appState.fileDragDepth = Math.max(0, appState.fileDragDepth - 1);
    if (appState.fileDragDepth === 0) setFileDropActive(false);
  });
  stage.addEventListener('drop', (event) => {
    const dragEvent = event as DragEvent;
    if (appState.sessionMode === 'view') return;
    if (!hasFiles(dragEvent.dataTransfer)) return;
    dragEvent.preventDefault();
    dragEvent.stopPropagation();
    const files = Array.from(dragEvent.dataTransfer?.files || []);
    clearDropState();
    void sendWebFiles(files);
  });

  // 某些浏览器允许把 Finder/资源管理器中复制的文件粘到页面；若浏览器
  // 暴露了文件对象，就直接复用同一条独立文件通道，不依赖系统文件剪贴板。
  stage.addEventListener('paste', (event) => {
    if (appState.sessionMode === 'view') return;
    const target = event.target as HTMLElement | null;
    if (target?.closest('input, textarea, select, [contenteditable="true"]')) return;
    const files = Array.from((event as ClipboardEvent).clipboardData?.files || []);
    if (files.length === 0) return;
    event.preventDefault();
    void sendWebFiles(files);
  });
  window.addEventListener('dragend', clearDropState);
  window.addEventListener('blur', clearDropState);
  restorePersistedFileTasks();
  updateFileTransferButton();
  renderFileTransfers();
}

export { bindFileTransfer };
